import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { getApiUrl } from "@/lib/api-config";

/**
 * Settings > Plans: the one plan KrewUp offers today, Pro (USD 199 per month; AI usage billed separately from the actual consumption). Read-only:
 * there is no payment integration, so nothing here charges, upgrades or switches anything. The plans come from GET /api/ai-plans.
 */

interface PlanRow {
  name: string;
  label: string;
  monthly_price_usd: number;
  ai_usage: string;
}

export function PlansTab() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(getApiUrl("api/ai-plans"));
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) {
          throw new Error(result?.error || "Failed to load plans");
        }
        setPlans(result.plans || []);
        setCurrentPlan(result.current_plan || null);
      } catch (err) {
        console.error("Error loading AI plans:", err);
        setError(err instanceof Error ? err.message : "Failed to load plans");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="p-6">
          <p className="text-sm text-gray-500">Loading plans...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="p-6">
          <p className="text-sm text-red-600" role="alert">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 gap-4">
          {plans.map((plan) => {
            // a company on an older technical plan still sees the one plan we offer; it is marked current only when it really is
            const isCurrent = plan.name === currentPlan;
            return (
              <div key={plan.name} className={`rounded-lg border p-4 ${isCurrent ? "border-gray-900 bg-gray-50" : "border-gray-200"}`} data-testid={`plan-${plan.name}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{plan.label}</span>
                  {isCurrent && (
                    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-900 text-white">Current</span>
                  )}
                </div>
                <p className="text-2xl font-semibold">
                  ${plan.monthly_price_usd}
                  <span className="text-sm font-normal text-gray-500"> USD/month</span>
                </p>
                <p className="mt-2 text-sm text-gray-600">AI usage billed separately based on actual consumption.</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
